import axios from 'axios';
import { API_BASE } from '../config/api';

const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_LIST_LIMIT = 24;
const MAX_LIST_LIMIT = 60;

function currentToken() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('athena_token') || '';
}

function defaultUnauthorizedHandler({ token } = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('athena-auth-expired', { detail: { token } }));
}

function boundedLimit(value, fallback = DEFAULT_LIST_LIMIT) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.min(Math.floor(parsed), MAX_LIST_LIMIT));
}

function compactParams(values) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => (
    value !== undefined && value !== null && value !== ''
  )));
}

function safeEntityId(id, name = 'id') {
  const value = String(id || '').trim();
  if (!value) throw new Error(`LIFE_OS_${name.toUpperCase()}_REQUIRED`);
  return encodeURIComponent(value);
}

/**
 * A small, authenticated boundary shared by Home, Workspace and Floating
 * Athena. It intentionally returns server data unchanged: the presentation
 * layer can keep source-of-truth fields such as statuses and timestamps.
 */
export function createLifeOSClient({
  apiBase = API_BASE,
  getToken = currentToken,
  onUnauthorized = defaultUnauthorizedHandler,
  timeout = DEFAULT_TIMEOUT_MS,
} = {}) {
  const api = axios.create({
    baseURL: String(apiBase || API_BASE).replace(/\/$/, ''),
    timeout,
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = getToken?.() || '';
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  api.interceptors.response.use(
    response => response,
    (error) => {
      // A forbidden resource is not necessarily an expired session. Only an
      // explicit unauthenticated response may trigger the app-level session
      // boundary.
      if (error?.response?.status === 401) {
        try { onUnauthorized?.({ token: getToken?.() || '', error }); } catch { /* best effort */ }
      }
      return Promise.reject(error);
    },
  );

  const get = (url, config = {}) => api.get(url, config).then(response => response.data);
  const post = (url, body, config = {}) => api.post(url, body, config).then(response => response.data);
  const patch = (url, body, config = {}) => api.patch(url, body, config).then(response => response.data);
  const remove = (url, config = {}) => api.delete(url, config).then(response => response.data);

  return {
    raw: api,

    getToday({ date, timezone, signal } = {}) {
      return get('/life/today', {
        signal,
        params: compactParams({ date, timezone }),
      });
    },

    // Privacy-preserving Life OS telemetry: the server accepts only a fixed
    // event name and surface, never message/capture/search content or IDs.
    trackTechnicalEvent({ eventName, event_name, surface }, { signal } = {}) {
      return post('/life/technical-events', {
        event_name: eventName || event_name,
        surface,
      }, { signal });
    },

    getInbox({ status, cursor, limit = DEFAULT_LIST_LIMIT, signal } = {}) {
      return get('/life/inbox', {
        signal,
        params: compactParams({ status, cursor, limit: boundedLimit(limit) }),
      });
    },

    createInbox(payload, { signal } = {}) {
      return post('/life/inbox', payload, { signal });
    },

    reviewInbox(itemId, action, payload = {}, { signal } = {}) {
      return patch(`/life/inbox/${safeEntityId(itemId, 'inbox_item')}`, { ...payload, action }, { signal });
    },

    getRoutines({ status, limit = DEFAULT_LIST_LIMIT, signal } = {}) {
      return get('/life/routines', {
        signal,
        params: compactParams({ status, limit: boundedLimit(limit) }),
      });
    },

    createRoutine(payload, { signal } = {}) {
      return post('/life/routines', payload, { signal });
    },

    updateRoutine(routineId, payload, { signal } = {}) {
      return patch(`/life/routines/${safeEntityId(routineId, 'routine')}`, payload, { signal });
    },

    skipRoutine(routineId, payload = {}, { signal } = {}) {
      return post(`/life/routines/${safeEntityId(routineId, 'routine')}/skip`, payload, { signal });
    },

    search({ query, q, types, limit = DEFAULT_LIST_LIMIT, signal } = {}) {
      const searchQuery = typeof query === 'string' ? query : q;
      return get('/life/search', {
        signal,
        params: compactParams({
          q: searchQuery?.trim(),
          types: Array.isArray(types) ? types.join(',') : types,
          limit: boundedLimit(limit),
        }),
      });
    },

    /** Normalized Vault endpoint; it includes typed library items and files. */
    getVault({ query, q, type, category, projectId, cursor, limit = DEFAULT_LIST_LIMIT, signal } = {}) {
      return get('/life/vault', {
        signal,
        params: compactParams({
          q: typeof query === 'string' ? query.trim() : q,
          type,
          category,
          project_id: projectId,
          cursor,
          limit: boundedLimit(limit),
        }),
      });
    },

    // Existing library endpoints remain available for legacy Workspace flows
    // and for integrations that need the original library data model.
    getLibraryItems({ folderId, category, favorite, signal } = {}) {
      return get('/library/items', {
        signal,
        params: compactParams({
          folder_id: folderId,
          category,
          favorite: favorite === undefined ? undefined : String(Boolean(favorite)),
        }),
      });
    },

    getLibraryCounts({ signal } = {}) {
      return get('/library/counts', { signal });
    },

    getLibraryFolders({ signal } = {}) {
      return get('/library/folders', { signal });
    },

    createLibraryFolder(payload, { signal } = {}) {
      return post('/library/folders', payload, { signal });
    },

    updateLibraryFolder(folderId, payload, { signal } = {}) {
      return patch(`/library/folders/${safeEntityId(folderId, 'library_folder')}`, payload, { signal });
    },

    deleteLibraryFolder(folderId, payload = {}, { signal } = {}) {
      return remove(`/library/folders/${safeEntityId(folderId, 'library_folder')}`, { data: payload, signal });
    },

    createLibraryItem(payload, { signal } = {}) {
      return post('/library/items', payload, { signal });
    },

    updateLibraryItem(itemId, payload, { signal } = {}) {
      return patch(`/library/items/${safeEntityId(itemId, 'library_item')}`, payload, { signal });
    },

    deleteLibraryItem(itemId, { signal } = {}) {
      return remove(`/library/items/${safeEntityId(itemId, 'library_item')}`, { signal });
    },

    getProjects({ signal } = {}) {
      return get('/projects', { signal });
    },
  };
}

export const lifeOSApi = createLifeOSClient();

export function isLifeOSRequestCancelled(error) {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED';
}

export function lifeOSErrorMessage(error, fallback = 'Não foi possível carregar estes dados agora.') {
  if (isLifeOSRequestCancelled(error)) return '';
  const detail = error?.response?.data?.error
    || error?.response?.data?.detail
    || error?.response?.data?.message;
  return typeof detail === 'string' && detail.trim() ? detail.trim() : fallback;
}

export const LIFE_OS_LIST_LIMITS = {
  default: DEFAULT_LIST_LIMIT,
  max: MAX_LIST_LIMIT,
};
